import React, { useState, useEffect, useRef } from "react";
import {
  Leaf, TrendingUp, ShieldCheck, ArrowRight, Sprout, ShoppingBag,
  Facebook, Twitter, Instagram, Linkedin, Menu, X, CheckCircle2,
  Calendar, ArrowUpRight, Lock, AlertTriangle, Building2
} from "lucide-react";
import { Helmet } from "react-helmet";
import { getFirestore, collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import app from "./firebase";

const db = getFirestore(app);

export default function AgrivestiaLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [heroTab, setHeroTab] = useState("buy"); // 'buy' | 'sell' | 'invest'
  const heroRef = useRef(null);

  // Hero Qualification Form state
  const [heroFormData, setHeroFormData] = useState({
    commodity: "Cocoa Beans",
    volume: "",
    location: "",
    phone: "",
    email: "",
    investmentRange: "₦500k – ₦2M"
  });
  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [heroLoading, setHeroLoading] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const fetchLatestBlogs = async () => {
      try {
        const q = query(collection(db, "blogs"), orderBy("timestamp", "desc"), limit(3));
        const snap = await getDocs(q);
        setLatestBlogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching latest blogs:", error);
      }
    };
    fetchLatestBlogs();
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    setHeroLoading(true);
    try {
      await addDoc(collection(db, `${heroTab}_leads`), {
        ...heroFormData,
        tab: heroTab,
        source: "homepage_hero",
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.warn("Firestore save fallback:", err);
    }
    setHeroLoading(false);
    setHeroSubmitted(true);
  };

  const nav = () => setMobileOpen(false);

  const stats = [
    { value: "1,200+", label: "Verified Suppliers & Buyers" },
    { value: "₦2.4B+", label: "Traded Volume" },
    { value: "100%", label: "Escrow Protected Payouts" },
    { value: "< 24h", label: "Supplier Match Speed" },
  ];

  const caseStudies = [
    {
      company: "AgriProcure International",
      crop: "Fermented Cocoa Beans",
      volume: "120 Metric Tons",
      location: "Ondo State → Antwerp Port",
      outcome: "Quality verified (Grade A), delivered 3 days ahead of schedule via Agrivestia Escrow."
    },
    {
      company: "WestAfrica Commodities Ltd",
      crop: "Raw Cashew Nuts (KOR 49)",
      volume: "250 Metric Tons",
      location: "Ogbomoso → Vietnam Processing",
      outcome: "Zero moisture disputes; funds released seamlessly upon SGS port inspection."
    },
    {
      company: "Sahel Oilseed Processors",
      crop: "White Sesame Seeds (99.5% Purity)",
      volume: "80 Metric Tons",
      location: "Jigawa → Lagos Local Plant",
      outcome: "Direct farmgate sourcing eliminated broker markups by 18%."
    }
  ];

  const linkStyle = { color: "#22c55e", fontWeight: 600 };

  return (
    <div style={{ fontFamily: "var(--font-sans)", color: "var(--color-gray-800)", overflowX: "hidden" }}>
      <Helmet>
        <title>Agrivestia — Source Verified Bulk Crops & Farm Investment Briefings in Africa</title>
        <meta name="description" content="Source verified bulk cocoa, cashew, sesame, and grains. Match with pre-inspected African suppliers, secure transactions with bank escrow, and access transparent farm opportunities." />
        <meta name="keywords" content="buy bulk crops Nigeria, verified agricultural suppliers Africa, sell crops online, farm investment opportunities, commodity escrow trading" />
      </Helmet>

      {/* ════════════════════════════════════════════════════════ NAVBAR */}
      <nav style={{
        position: "sticky", top: 0, zIndex: "var(--z-sticky)",
        background: scrolled ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        borderBottom: scrolled ? "1px solid var(--color-gray-100)" : "1px solid transparent",
        boxShadow: scrolled ? "var(--shadow-md)" : "none",
        transition: "all 0.3s ease",
        padding: "0 clamp(16px, 4vw, 60px)",
        height: "70px", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
          onClick={() => window.location.href = "/"}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "10px",
            background: "var(--gradient-accent)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <Leaf size={18} color="white" />
          </div>
          <span style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>
            AGRIVESTIA
          </span>
        </div>

        {/* Desktop Nav */}
        <div style={{ display: "flex", alignItems: "center", gap: "28px" }} className="hide-mobile">
          <span className="nav-link" onClick={() => window.location.href = "/buy-bulk-crops"}>Buy Crops</span>
          <span className="nav-link" onClick={() => window.location.href = "/sell-crops"}>Sell Crops</span>
          <span className="nav-link" onClick={() => window.location.href = "/farm-investments"}>Investments</span>
          <span className="nav-link" onClick={() => window.location.href = "/central_market"}>Marketplace</span>
          <span className="nav-link" onClick={() => window.location.href = "/blog"}>Insights</span>
          <button className="btn-ghost" onClick={() => window.location.href = "/login"} style={{ padding: "8px 18px", fontSize: "14px" }}>Log In</button>
          <button className="btn-primary" onClick={() => window.location.href = "/buy-bulk-crops"} style={{ padding: "10px 20px", fontSize: "14px" }}>
            Request Suppliers
          </button>
        </div>

        {/* Mobile hamburger */}
        <button className="show-mobile" onClick={() => setMobileOpen(true)}
          style={{ background: "none", border: "none", color: "var(--color-gray-700)", padding: "6px", cursor: "pointer" }}>
          <Menu size={24} />
        </button>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div className="overlay" onClick={() => setMobileOpen(false)} />
          <div className="show-mobile" style={{
            position: "fixed", top: 0, left: 0, width: "80%", maxWidth: "320px",
            height: "100vh", background: "var(--color-white)", zIndex: "var(--z-modal)",
            padding: "24px", boxShadow: "var(--shadow-xl)",
            animation: "slideInLeft 0.3s cubic-bezier(0.16,1,0.3,1)",
            display: "flex", flexDirection: "column",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Leaf size={16} color="white" />
                </div>
                <span style={{ fontWeight: 800, fontSize: "18px", color: "var(--color-primary-dark)" }}>AGRIVESTIA</span>
              </div>
              <button onClick={() => setMobileOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-500)" }}>
                <X size={22} />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, overflowY: "auto" }}>
              {[
                { label: "For Commodity Buyers", href: "/buy-bulk-crops" },
                { label: "For Crop Sellers / Farmers", href: "/sell-crops" },
                { label: "Farm Opportunities", href: "/farm-investments" },
                { label: "Central Marketplace", href: "/central_market" },
                { label: "Blog & Insights", href: "/blog" },
                { label: "Contact Team", href: "/contact" },
              ].map(({ label, href }) => (
                <button key={label} onClick={() => { nav(); window.location.href = href; }}
                  style={{ background: "none", border: "none", textAlign: "left", padding: "14px 12px", fontSize: "16px", fontWeight: 500, color: "var(--color-gray-700)", cursor: "pointer", borderRadius: "var(--radius-md)" }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "24px", borderTop: "1px solid var(--color-gray-100)" }}>
              <button className="btn-ghost" onClick={() => { nav(); window.location.href = "/login"; }} style={{ width: "100%", justifyContent: "center" }}>Log In</button>
              <button className="btn-primary" onClick={() => { nav(); window.location.href = "/buy-bulk-crops"; }} style={{ width: "100%", justifyContent: "center" }}>Request Suppliers</button>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════ HERO */}
      <section ref={heroRef} style={{
        minHeight: "88vh", display: "flex", alignItems: "center",
        background: "linear-gradient(180deg, #052e16 0%, #0f172a 100%)",
        padding: "60px clamp(20px, 5vw, 80px) 80px",
        position: "relative", overflow: "hidden", color: "white"
      }}>
        {/* Background pattern */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "36px 36px",
        }} />

        <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto", position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "48px", alignItems: "center" }} className="hero-grid" id="hero-grid">

            {/* Left Column Copy */}
            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)",
                borderRadius: "20px", padding: "6px 14px", marginBottom: "20px",
              }}>
                <ShieldCheck size={16} color="#4ade80" />
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#4ade80", letterSpacing: "0.03em" }}>Verified Origin Suppliers &amp; Escrow Trade</span>
              </div>

              <h1 style={{
                fontSize: "clamp(34px, 4.5vw, 56px)", fontWeight: 900, lineHeight: 1.15,
                color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "20px",
              }}>
                Source verified bulk crops from trusted African suppliers.
              </h1>

              <p style={{ fontSize: "clamp(16px, 1.8vw, 19px)", color: "#cbd5e1", lineHeight: 1.6, maxWidth: "520px", marginBottom: "32px" }}>
                Tell us the crop, volume, and delivery location. Agrivestia matches qualified buyers with verified suppliers and supports secure trade from request to delivery.
              </p>

              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "36px" }}>
                <button className="btn-primary"
                  onClick={() => window.location.href = "/buy-bulk-crops"}
                  style={{ padding: "14px 28px", fontSize: "16px", background: "#16a34a", color: "#fff", fontWeight: 700, borderRadius: "8px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                  Request Verified Suppliers <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => window.location.href = "/sell-crops"}
                  style={{ padding: "14px 24px", fontSize: "16px", background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>
                  I Want to Sell Crops
                </button>
              </div>

              {/* Quick stats strip */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: "20px" }}>
                {stats.map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: "#4ade80" }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Hero Qualification Form Widget */}
            <div style={{ background: "#ffffff", borderRadius: "16px", padding: "28px", color: "#0f172a", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)" }}>
              <div style={{ display: "flex", borderBottom: "2px solid #f1f5f9", marginBottom: "20px" }}>
                <button
                  onClick={() => { setHeroTab("buy"); setHeroSubmitted(false); }}
                  style={{ flex: 1, padding: "10px", border: "none", background: "none", fontWeight: 700, fontSize: "14px", cursor: "pointer", color: heroTab === "buy" ? "#16a34a" : "#64748b", borderBottom: heroTab === "buy" ? "2px solid #16a34a" : "none", marginBottom: "-2px" }}>
                  I Want to Buy
                </button>
                <button
                  onClick={() => { setHeroTab("sell"); setHeroSubmitted(false); }}
                  style={{ flex: 1, padding: "10px", border: "none", background: "none", fontWeight: 700, fontSize: "14px", cursor: "pointer", color: heroTab === "sell" ? "#16a34a" : "#64748b", borderBottom: heroTab === "sell" ? "2px solid #16a34a" : "none", marginBottom: "-2px" }}>
                  I Want to Sell
                </button>
                <button
                  onClick={() => { setHeroTab("invest"); setHeroSubmitted(false); }}
                  style={{ flex: 1, padding: "10px", border: "none", background: "none", fontWeight: 700, fontSize: "14px", cursor: "pointer", color: heroTab === "invest" ? "#16a34a" : "#64748b", borderBottom: heroTab === "invest" ? "2px solid #16a34a" : "none", marginBottom: "-2px" }}>
                  Invest in Farms
                </button>
              </div>

              {heroSubmitted ? (
                <div style={{ textAlign: "center", padding: "24px 8px" }}>
                  <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <CheckCircle2 size={28} />
                  </div>
                  <h3 style={{ fontSize: "20px", fontWeight: 800, marginBottom: "8px" }}>Request Submitted!</h3>
                  <p style={{ fontSize: "14px", color: "#475569", lineHeight: 1.5, marginBottom: "20px" }}>
                    Our desk has received your {heroTab} request for <strong>{heroFormData.commodity}</strong>. A specialist will follow up on WhatsApp/phone within 1 business day.
                  </p>
                  <a href={heroTab === "buy" ? "/buy-bulk-crops" : heroTab === "sell" ? "/sell-crops" : "/farm-investments"}
                    style={{ background: "#16a34a", color: "#fff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px", display: "inline-block" }}>
                    View Full {heroTab.toUpperCase()} Sourcing Page &rarr;
                  </a>
                </div>
              ) : (
                <form onSubmit={handleHeroSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
                    {heroTab === "buy" && "Request Verified Suppliers Fast"}
                    {heroTab === "sell" && "Check Verified Buyer Demand"}
                    {heroTab === "invest" && "Receive Farm Opportunity Briefing"}
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>Commodity / Crop *</label>
                    <select value={heroFormData.commodity} onChange={(e) => setHeroFormData({ ...heroFormData, commodity: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }}>
                      <option>Cocoa Beans</option>
                      <option>Raw Cashew Nuts</option>
                      <option>White Sesame Seeds</option>
                      <option>Paddy Rice / Corn</option>
                      <option>Split Ginger</option>
                    </select>
                  </div>

                  {heroTab === "invest" ? (
                    <div>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>Investment Target Range</label>
                      <select value={heroFormData.investmentRange} onChange={(e) => setHeroFormData({ ...heroFormData, investmentRange: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }}>
                        <option>₦500,000 – ₦2,000,000</option>
                        <option>₦2,000,000 – ₦10,000,000</option>
                        <option>₦10,000,000+</option>
                      </select>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>Estimated Volume *</label>
                        <input type="text" required placeholder="e.g. 25 MT" value={heroFormData.volume} onChange={(e) => setHeroFormData({ ...heroFormData, volume: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>{heroTab === "buy" ? "Delivery Port/City" : "Farm State"}</label>
                        <input type="text" required placeholder="e.g. Lagos Port" value={heroFormData.location} onChange={(e) => setHeroFormData({ ...heroFormData, location: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                      </div>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>WhatsApp / Phone *</label>
                      <input type="tel" required placeholder="+234 800 000 0000" value={heroFormData.phone} onChange={(e) => setHeroFormData({ ...heroFormData, phone: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "4px" }}>Email Address *</label>
                      <input type="email" required placeholder="name@company.com" value={heroFormData.email} onChange={(e) => setHeroFormData({ ...heroFormData, email: e.target.value })} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                    </div>
                  </div>

                  <button type="submit" disabled={heroLoading} style={{ background: "#16a34a", color: "#ffffff", border: "none", padding: "12px", borderRadius: "8px", fontWeight: 800, fontSize: "15px", cursor: "pointer", marginTop: "4px" }}>
                    {heroLoading ? "Submitting..." : heroTab === "buy" ? "Request Verified Suppliers" : heroTab === "sell" ? "Get Free Buyer-Demand Check" : "Receive Farm Opportunities"}
                  </button>
                  <div style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center" }}>
                    🔒 No account creation required upfront. Zero obligation.
                  </div>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ TRUST BAR */}
      <section style={{ background: "#f0fdf4", padding: "20px clamp(20px,5vw,80px)", borderBottom: "1px solid #bbf7d0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: "32px", flexWrap: "wrap", fontSize: "14px", fontWeight: 700, color: "#166534" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#16a34a" /> Verified Suppliers</div>
          <div>•</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><Lock size={18} color="#16a34a" /> Escrow-Supported Transactions</div>
          <div>•</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><Building2 size={18} color="#16a34a" /> Local & Export Sourcing</div>
          <div>•</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><Calendar size={18} color="#16a34a" /> Response Within 1 Business Day</div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ AUDIENCE SEGMENTATION CARDS */}
      <section style={{ padding: "80px clamp(20px,5vw,80px)", background: "#ffffff" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 900, color: "#0f172a", marginBottom: "12px" }}>Choose Your Trading Path</h2>
          <p style={{ color: "#64748b", fontSize: "16px", maxWidth: "600px", margin: "0 auto 48px" }}>Agrivestia tailors information, proof, and next steps for every side of agricultural trade.</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "32px", textAlign: "left" }}>
            {/* Card 1: Buyers */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "32px", display: "flex", flexDirection: "column", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                <ShoppingBag size={24} />
              </div>
              <h3 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "10px" }}>For Commodity Buyers</h3>
              <p style={{ color: "#64748b", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px", flex: 1 }}>
                Source bulk crops without unreliable middlemen. Get matched with pre-inspected African suppliers with lab-verified moisture and outturn quality specs.
              </p>
              <a href="/buy-bulk-crops" style={{ background: "#16a34a", color: "#ffffff", padding: "12px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                Request Verified Suppliers <ArrowRight size={16} />
              </a>
            </div>

            {/* Card 2: Sellers */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "32px", display: "flex", flexDirection: "column", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                <Sprout size={24} />
              </div>
              <h3 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "10px" }}>For Sellers &amp; Farmers</h3>
              <p style={{ color: "#64748b", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px", flex: 1 }}>
                Find verified buyers for your harvest. Access corporate processors and exporters ready to purchase your produce with guaranteed escrow payouts.
              </p>
              <a href="/sell-crops" style={{ background: "#052e16", color: "#ffffff", padding: "12px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                Get Free Buyer-Demand Check <ArrowRight size={16} />
              </a>
            </div>

            {/* Card 3: Investors */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "32px", display: "flex", flexDirection: "column", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                <TrendingUp size={24} />
              </div>
              <h3 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "10px" }}>For Farm Investors</h3>
              <p style={{ color: "#64748b", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px", flex: 1 }}>
                Review farm opportunities with transparent terms, clear risk disclosures, satellite field tracking, and off-take backed trade financing.
              </p>
              <a href="/farm-investments" style={{ background: "#1e293b", color: "#ffffff", padding: "12px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                Receive Current Farm Briefing <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ WHY BUSINESSES TRUST AGRIVESTIA */}
      <section style={{ padding: "80px clamp(20px,5vw,80px)", background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <span style={{ background: "#dcfce7", color: "#16a34a", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 700 }}>PROOF & SECURITY</span>
            <h2 style={{ fontSize: "34px", fontWeight: 900, color: "#0f172a", marginTop: "12px", marginBottom: "12px" }}>Why Businesses Trust Agrivestia</h2>
            <p style={{ color: "#64748b", fontSize: "16px", maxWidth: "640px", margin: "0 auto" }}>
              Verifiable proof, transparent supplier checks, and bank-grade escrow protection directly near the decision point.
            </p>
          </div>

          {/* 4-Step Verification */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "24px", marginBottom: "64px" }}>
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, marginBottom: "16px" }}>1</div>
              <h3 style={{ fontSize: "17px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Supplier Verification</h3>
              <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>CAC registration check, physical warehouse audit, and historical fulfillment rating before any supplier can quote.</p>
            </div>
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, marginBottom: "16px" }}>2</div>
              <h3 style={{ fontSize: "17px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Escrow Protection</h3>
              <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Buyer funds are held safely in escrow. Money is released only after independent destination quality inspection.</p>
            </div>
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, marginBottom: "16px" }}>3</div>
              <h3 style={{ fontSize: "17px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Lab Quality Checks</h3>
              <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Moisture testing, KOR outturn ratios, and purity certification matching SGS export standards.</p>
            </div>
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, marginBottom: "16px" }}>4</div>
              <h3 style={{ fontSize: "17px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Clear Delivery Timeline</h3>
              <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Live dispatch tracking from farmgate warehouse to delivery port within guaranteed 5–14 days.</p>
            </div>
          </div>

          {/* Real Named Client Case Studies */}
          <h3 style={{ fontSize: "24px", fontWeight: 900, color: "#0f172a", marginBottom: "20px" }}>Verified Transaction Case Studies</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px", marginBottom: "64px" }}>
            {caseStudies.map((cs, i) => (
              <div key={i} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", marginBottom: "6px" }}>Verified Deal • {cs.crop}</div>
                <h4 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>{cs.company}</h4>
                <div style={{ fontSize: "14px", color: "#475569", marginBottom: "12px" }}>
                  <strong>Volume:</strong> {cs.volume} | <strong>Route:</strong> {cs.location}
                </div>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6, background: "#f8fafc", padding: "12px", borderRadius: "8px", margin: 0 }}>
                  "{cs.outcome}"
                </p>
              </div>
            ))}
          </div>

          {/* Risk Disclosure Callout Box */}
          <div style={{ background: "#fffbebf0", border: "1px solid #fef08a", borderRadius: "12px", padding: "24px", color: "#78350f" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: 800, fontSize: "16px", marginBottom: "8px", color: "#92400e" }}>
              <AlertTriangle size={20} /> Plain-Language Regulatory &amp; Risk Disclosure
            </div>
            <p style={{ fontSize: "14px", lineHeight: 1.6, margin: 0 }}>
              Agricultural trading and farming investments carry inherent operational, weather, and commodity price risks. Agrivestia provides transparent contract structures, satellite monitoring, and insured escrow capital allocation, but does not guarantee fixed financial returns. Investors must review full project risk documentation prior to committing funds.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ HIGH-INTENT SEARCH PAGES NAVIGATION */}
      <section style={{ padding: "60px clamp(20px,5vw,80px)", background: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h3 style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>Popular Commercial Sourcing Hubs</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {[
              { label: "Buy Bulk Cocoa Nigeria", href: "/buy-bulk-cocoa-nigeria" },
              { label: "Buy Raw Cashew Nuts Nigeria", href: "/buy-cashew-nigeria" },
              { label: "Buy White Sesame Seeds", href: "/buy-sesame-seeds-nigeria" },
              { label: "Verified Agricultural Suppliers", href: "/verified-agricultural-suppliers-nigeria" },
              { label: "Sell Crops to Bulk Buyers", href: "/sell-crops-to-bulk-buyers" },
              { label: "Farm Investment Opportunities", href: "/farm-investment-opportunities-nigeria" },
              { label: "Commodity Market Prices", href: "/agricultural-commodity-prices-nigeria" },
              { label: "Export Agricultural Produce", href: "/export-agricultural-produce-from-nigeria" },
            ].map(item => (
              <a key={item.label} href={item.href} style={{ background: "#f1f5f9", color: "#334155", padding: "8px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: 600, textDecoration: "none", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#16a34a"; e.currentTarget.style.color = "#ffffff"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#334155"; }}>
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ BLOG SECTION */}
      <section style={{ padding: "80px clamp(20px,5vw,80px)", background: "#f8fafc" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "40px", flexWrap: "wrap", gap: "24px" }}>
            <div>
              <span style={{ background: "#dcfce7", color: "#16a34a", padding: "4px 12px", borderRadius: "16px", fontSize: "12px", fontWeight: 700 }}>AGRICULTURAL INSIGHTS</span>
              <h2 style={{ fontSize: "30px", fontWeight: 900, color: "#0f172a", marginTop: "8px" }}>From Our Agribusiness Desk</h2>
            </div>
            <button onClick={() => window.location.href = "/blog"} className="btn-ghost" style={{ padding: "10px 20px", display: "flex", alignItems: "center", gap: "8px" }}>
              View All Insights <ArrowUpRight size={18} />
            </button>
          </div>

          {latestBlogs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px", background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
              <p style={{ color: "#64748b", margin: 0 }}>Articles loading... Explore our <a href="/blog" style={linkStyle}>Blog Desk</a>.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
              {latestBlogs.map((blog, i) => (
                <div key={blog.id} onClick={() => window.location.href = "/blog"} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", cursor: "pointer" }}>
                  <div style={{ height: "180px", position: "relative" }}>
                    <img src={blog.image || "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=600&q=80"} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div style={{ padding: "20px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>{blog.title}</h3>
                    <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.5 }}>{blog.content.substring(0, 90)}...</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ FOOTER */}
      <footer style={{ background: "#052e16", color: "rgba(255,255,255,0.75)", padding: "60px clamp(20px,5vw,80px) 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr", gap: "48px", marginBottom: "48px" }} id="footer-grid">
            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Leaf size={16} color="white" />
                </div>
                <span style={{ fontSize: "18px", fontWeight: 800, color: "white" }}>AGRIVESTIA</span>
              </div>
              <p style={{ fontSize: "14px", lineHeight: 1.7, marginBottom: "24px", maxWidth: "280px" }}>
                Africa's trusted agricultural commodity marketplace &amp; agribusiness trade financing platform.
              </p>
              <div style={{ display: "flex", gap: "12px" }}>
                {[
                  { icon: <Facebook size={18} />, href: "https://web.facebook.com/profile.php?id=61575104505321" },
                  { icon: <Twitter size={18} />, href: "https://twitter.com" },
                  { icon: <Instagram size={18} />, href: "https://instagram.com" },
                  { icon: <Linkedin size={18} />, href: "https://www.linkedin.com/company/107015891" },
                ].map(({ icon, href }, i) => (
                  <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ width: "36px", height: "36px", borderRadius: "6px", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                    {icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Platform */}
            <div>
              <h4 style={{ color: "white", fontWeight: 700, fontSize: "14px", textTransform: "uppercase", marginBottom: "20px" }}>Platform</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "14px" }}>
                <a href="/buy-bulk-crops" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Buy Bulk Crops</a>
                <a href="/sell-crops" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Sell Your Harvest</a>
                <a href="/farm-investments" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Farm Opportunities</a>
                <a href="/central_market" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Central Market</a>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 style={{ color: "white", fontWeight: 700, fontSize: "14px", textTransform: "uppercase", marginBottom: "20px" }}>Company</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "14px" }}>
                <a href="/blog" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Blog Desk</a>
                <a href="/contact" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Contact Team</a>
                <a href="/privacy-policy" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Privacy Policy</a>
                <a href="/terms-of-service" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Terms of Service</a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 style={{ color: "white", fontWeight: 700, fontSize: "14px", textTransform: "uppercase", marginBottom: "20px" }}>Contact Desk</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "14px" }}>
                <div>Email: support@agrivestia.com</div>
                <div>Phone: +234 800 AGRIVESTIA</div>
                <div>Lagos, Nigeria • Accra, Ghana</div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", fontSize: "13px" }}>
            <span>© {new Date().getFullYear()} Agrivestia. All rights reserved.</span>
            <div>Escrow payments and verification powered by Agrivestia Trade Desk.</div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 900px) {
          #hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          #footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 600px) {
          #footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}